import {
  BrowserIcon,
  DatabaseIcon,
  NavigationArrowIcon,
  LockIcon,
  LockOpenIcon,
  BugIcon,
  SignOutIcon,
  TranslateIcon,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useState } from "react";

import { isSuperQi, isWeb } from "@/app/platform";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { Divider } from "@/components/Divider/Divider";
import { Menu, MenuItem } from "@/components/Menu";
import { vConsole } from "@/debug/vConsole";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { type Locale } from "@/utils/locale";
import { devLocaleStorage } from "@/utils/localStorage";
import { accessTokenStorage, refreshTokenStorage } from "@/utils/tokens";

import type { ActivePanel } from "./DevToolsHub";
import type { UsePurchaseBlockReturn } from "./tools/usePurchaseBlock";

interface DevToolsMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  activePanel: ActivePanel;
  onTogglePanel: (panel: NonNullable<ActivePanel>) => void;
  purchaseBlock: UsePurchaseBlockReturn;
}

export function DevToolsMenu({
  anchorEl,
  onClose,
  activePanel,
  onTogglePanel,
  purchaseBlock: {
    isPurchaseAllowed,
    purchaseAllowedSecondsLeft,
    togglePurchaseBlock,
  },
}: DevToolsMenuProps) {
  const { invalidate } = useRouter();

  const { locale } = useLocaleInfo();
  const nextLocale: Locale = locale === "ar" ? "en" : "ar";

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isSuperQiHeaderVisible = window !== window.top;

  return (
    <Menu
      id="dev-tools-menu"
      triggerId="dev-tools-trigger"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      dir="ltr"
      disableScrollLock
      position="top-end"
    >
      {!!vConsole && (
        <MenuItem
          selected={activePanel === "console"}
          startIcon={<BugIcon size={20} />}
          onClick={() => {
            onTogglePanel("console");
            onClose();
          }}
          label="Console"
        />
      )}

      <MenuItem
        selected={activePanel === "rq"}
        startIcon={<DatabaseIcon size={20} />}
        onClick={() => {
          onTogglePanel("rq");
          onClose();
        }}
        label="React Query"
      />

      <MenuItem
        selected={activePanel === "router"}
        startIcon={<NavigationArrowIcon size={20} />}
        onClick={() => {
          onTogglePanel("router");
          onClose();
        }}
        label="Router"
      />

      <Divider />

      {isWeb && (
        <MenuItem
          disabled={isLoggingOut}
          startIcon={
            isLoggingOut ? (
              <CircularProgress size={20} />
            ) : (
              <SignOutIcon size={20} />
            )
          }
          onClick={async () => {
            setIsLoggingOut(true);

            try {
              accessTokenStorage.remove();
              refreshTokenStorage.remove();

              await invalidate();
            } catch (error) {
              Sentry.captureException(error);
            } finally {
              setIsLoggingOut(false);
              onClose();
            }
          }}
          label="Log out"
        />
      )}

      {!isSuperQi && (
        <MenuItem
          startIcon={<BrowserIcon size={20} />}
          onClick={() => {
            if (!isSuperQiHeaderVisible) {
              const currentPath =
                window.location.pathname + window.location.search;

              window.location.href = `/superQi.html?path=${encodeURIComponent(currentPath)}`;

              return;
            }

            const currentPath =
              window.location.pathname + window.location.search;
            window.top!.location.href = currentPath;
          }}
          label={`${isSuperQiHeaderVisible ? "Hide" : "Show"} SuperQi header`}
        />
      )}

      {!isSuperQi && (
        <MenuItem
          startIcon={<TranslateIcon size={20} />}
          secondaryLabel={`${locale.toUpperCase()} → ${nextLocale.toUpperCase()}`}
          onClick={() => {
            devLocaleStorage.set(nextLocale);
            onClose();
            window.location.reload();
          }}
          label="Language"
        />
      )}

      <MenuItem
        startIcon={
          isPurchaseAllowed ? (
            <LockOpenIcon size={20} className="text-semantic-error-strong" />
          ) : (
            <LockIcon size={20} />
          )
        }
        secondaryLabel={
          isPurchaseAllowed
            ? `Disables in ${dayjs.duration(purchaseAllowedSecondsLeft, "seconds").format("mm:ss")}`
            : "Tap to unlock"
        }
        onClick={() => {
          onClose();
          togglePurchaseBlock();
        }}
        label={
          <span
            className={isPurchaseAllowed ? "text-semantic-error-strong" : ""}
          >
            {isPurchaseAllowed ? "Purchases allowed" : "Purchases blocked"}
          </span>
        }
      />
    </Menu>
  );
}
